import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import ErrorStackParser from "error-stack-parser";
import StackTraceGPS from "stacktrace-gps";
import { Container, Paper, Title, Text, Button, Stack } from "@mantine/core";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

type LoggableError = {
  message: string;
  stack?: string;
  componentStack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
};

type StackFrameData = {
  functionName?: string;
  fileName?: string;
  lineNumber?: number;
  columnNumber?: number;
};


type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
};

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static instance: ErrorBoundary | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    ErrorBoundary.instance = this;
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

/*  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    logError("ErrorBoundary", {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }*/
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    // Just pass the actual Error
    logError("ErrorBoundary", error);
  }

  public triggerFallback(error: Error) {
    this.setState({ hasError: true, error });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Container size="sm" style={{ marginTop: 80 }}>
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Stack align="center" spacing="md">
              <Title order={2} color="red">
                Oops! Something went wrong
              </Title>
              <Text color="dimmed" size="sm" align="center">
                An unexpected error occurred. Please try reloading the page.
              </Text>
              <Button variant="filled" color="red" onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </Stack>
          </Paper>
        </Container>
      );
    }
    return this.props.children;
  }
}


function logError(type: string, error: LoggableError) {
  const gps = new StackTraceGPS();
  const frames = error.stack ? ErrorStackParser.parse(new Error(error.stack)) : [];

  Promise.all(
    frames.map(async (frame) => {
      try {
        const mapped = await gps.pinpoint(frame); // resolves original file via source maps
        return {
          functionName: mapped.functionName || "<anonymous>",
          fileName: mapped.fileName || "<unknown>",
          lineNumber: mapped.lineNumber || 0,
          columnNumber: mapped.columnNumber || 0,
        } as StackFrameData;
      } catch {
        return {
          functionName: frame.functionName || "<anonymous>",
          fileName: frame.fileName || "<unknown>",
          lineNumber: frame.lineNumber || 0,
          columnNumber: frame.columnNumber || 0,
        } as StackFrameData;
      }
    })
  ).then((parsedStack) => {
    const payload = {
      time: new Date().toISOString(),
      type,
      message: error.message,
      source: error.source || null,
      lineno: error.lineno || null,
      colno: error.colno || null,
      componentStack: error.componentStack || null,
      parsedStack,
    };

    fetch(
      (process.env.REACT_APP_BACKEND_API_BASE_URL || "") + "/logError",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    ).catch((err) => console.error("Failed to send error log:", err));
  });
}

window.onerror = function (
  message: string | Event,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
) {
  // Normalize to string
  const normalizedMessage =
    typeof message === "string" ? message : (message as Event).type;

      
    if (normalizedMessage?.includes("ResizeObserver loop")) {
      return true;
    }
 
 

  logError("window.onerror", {
    message: normalizedMessage,
    source,
    lineno,
    colno,
    stack: error?.stack,
  });

  if (error instanceof Error) {
    ErrorBoundary.instance?.triggerFallback(error);
  } else {
    ErrorBoundary.instance?.triggerFallback(new Error(normalizedMessage));
  }
};

window.onunhandledrejection = function (event) {
  const reason = event.reason;
  logError("unhandledrejection", {
    message: (reason as Error)?.message || reason,
    stack: (reason as Error)?.stack,
  });

  if (reason instanceof Error) {
    ErrorBoundary.instance?.triggerFallback(reason);
  } else {
    ErrorBoundary.instance?.triggerFallback(new Error(String(reason)));
  }
};

// Render App wrapped in ErrorBoundary
root.render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
